import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type { Language, SearchRecord, SearchStatus, SourceState } from '@sourcr/shared';
import { SOURCES, isDemoSource, isAssistSource } from '@sourcr/shared';
import { I18N } from '../../lib/i18n';
import { createSearch, sendMessage, getSearch, listMessages, rememberSearch, runSearch, retrySearch } from '../../lib/api';
import { AgentMarkdown, cleanAgentText } from '../../lib/agent-message';
import { IconSend, IconSpinner } from '../../components/icons';
import { SourceBadge } from '../../components/source-badge';
import { Toast, useToast } from '../../components/ui/toast';

interface ChatPageProps {
  lang: Language;
}

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  ts: string;
}

function makeInitialMessage(lang: Language): Message {
  return {
    id: 'sys-1',
    role: 'assistant',
    content:
      lang === 'uk'
        ? 'Привіт! Я ваш асистент з пошуку кандидатів. Розкажіть мені про вакансію — посаду, необхідні навички, локацію та рівень досвіду. Я допоможу знайти найкращих кандидатів.'
        : "Hi! I'm your sourcing assistant. Tell me about the role you're hiring for — the position, required skills, location, and experience level. I'll help you find the best candidates.",
    ts: new Date().toISOString(),
  };
}

const S = {
  page: {
    display: 'flex',
    gap: 20,
    height: 'calc(100dvh - 56px - 48px)',
    maxHeight: 'calc(100dvh - 120px)',
  },
  chatPanel: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column' as const,
    backgroundColor: 'var(--bg-surface)',
    border: '1px solid var(--border)',
    borderRadius: 12,
    overflow: 'hidden',
  },
  chatHeader: {
    padding: '14px 20px',
    borderBottom: '1px solid var(--border)',
    fontSize: 14,
    fontWeight: 600,
    color: 'var(--text-primary)',
  },
  messages: {
    flex: 1,
    overflowY: 'auto' as const,
    padding: '16px 20px',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 12,
  },
  msgUser: {
    alignSelf: 'flex-end',
    maxWidth: '80%',
    backgroundColor: 'var(--accent)',
    color: '#fff',
    padding: '10px 14px',
    borderRadius: '12px 12px 4px 12px',
    fontSize: 14,
    lineHeight: 1.5,
  },
  msgAssistant: {
    alignSelf: 'flex-start',
    maxWidth: '85%',
    backgroundColor: 'var(--bg-elevated)',
    color: 'var(--text-primary)',
    padding: '10px 14px',
    borderRadius: '4px 12px 12px 12px',
    fontSize: 14,
    lineHeight: 1.5,
    border: '1px solid var(--border)',
  },
  errorMsg: {
    alignSelf: 'flex-start',
    maxWidth: '85%',
    backgroundColor: 'var(--danger-muted, #fff0f0)',
    color: 'var(--danger, #e53e3e)',
    padding: '10px 14px',
    borderRadius: '4px 12px 12px 12px',
    fontSize: 13,
    border: '1px solid var(--danger, #e53e3e)',
  },
  inputRow: {
    padding: '12px 16px',
    borderTop: '1px solid var(--border)',
    display: 'flex',
    gap: 10,
    alignItems: 'flex-end',
  },
  textarea: {
    flex: 1,
    padding: '10px 12px',
    borderRadius: 10,
    border: '1px solid var(--border)',
    backgroundColor: 'var(--bg-base)',
    color: 'var(--text-primary)',
    fontSize: 14,
    resize: 'none' as const,
    outline: 'none',
    fontFamily: 'var(--font-sans)',
    lineHeight: 1.5,
    maxHeight: 120,
    overflowY: 'auto' as const,
  },
  sendBtn: {
    width: 40,
    height: 40,
    borderRadius: 10,
    border: 'none',
    backgroundColor: 'var(--accent)',
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    flexShrink: 0,
    transition: 'opacity 0.12s',
  },
  // P1: quick-reply chips under the latest clarifier question.
  suggestionRow: {
    alignSelf: 'flex-start',
    maxWidth: '85%',
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: 8,
    marginTop: -4,
  },
  suggestionChip: {
    padding: '6px 12px',
    borderRadius: 16,
    border: '1px solid var(--accent)',
    backgroundColor: 'transparent',
    color: 'var(--accent)',
    fontSize: 13,
    fontFamily: 'var(--font-sans)',
    lineHeight: 1.4,
    cursor: 'pointer',
    transition: 'opacity 0.12s',
  },
  rightPanel: {
    width: 280,
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 16,
  },
  card: {
    backgroundColor: 'var(--bg-surface)',
    border: '1px solid var(--border)',
    borderRadius: 12,
    overflow: 'hidden',
  },
  cardHeader: {
    padding: '12px 16px',
    borderBottom: '1px solid var(--border)',
    fontSize: 13,
    fontWeight: 600,
    color: 'var(--text-primary)',
  },
  cardBody: { padding: '12px 16px' },
  criteriaEmpty: {
    fontSize: 13,
    color: 'var(--text-muted)',
    textAlign: 'center' as const,
    padding: '12px 0',
  },
  criteriaRow: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 8,
  },
  criteriaItem: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 3,
  },
  criteriaLabel: { fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase' as const },
  criteriaValue: { fontSize: 13, color: 'var(--text-primary)' },
  sourceRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '6px 0',
  },
  sourceStatus: { fontSize: 12 },
  sourceStateLabel: {
    fontFamily: 'var(--font-mono)',
    fontSize: 10,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.04em',
  },
  demoTag: {
    fontFamily: 'var(--font-mono)',
    fontSize: 9,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.06em',
    color: 'var(--warning)',
    border: '1px solid color-mix(in oklch, var(--warning) 40%, var(--border))',
    borderRadius: 4,
    padding: '1px 5px',
  },
  assistTag: {
    fontFamily: 'var(--font-mono)',
    fontSize: 9,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.06em',
    color: 'var(--accent)',
    border: '1px solid color-mix(in oklch, var(--accent) 40%, var(--border))',
    borderRadius: 4,
    padding: '1px 5px',
  },
  actionBtn: {
    width: '100%',
    padding: '10px',
    borderRadius: 8,
    border: 'none',
    backgroundColor: 'var(--accent)',
    color: '#fff',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'opacity 0.12s',
  },
  newSearchBtn: {
    width: '100%',
    padding: '9px',
    borderRadius: 8,
    border: '1px solid var(--border)',
    backgroundColor: 'transparent',
    color: 'var(--text-secondary)',
    fontSize: 13,
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'opacity 0.12s',
  },
  searchIdBadge: {
    fontSize: 11,
    color: 'var(--text-muted)',
    fontFamily: 'var(--font-mono)',
    padding: '2px 4px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
  },
  // Skeleton styles for message history loading
  skeletonMsg: (alignRight: boolean): React.CSSProperties => ({
    alignSelf: alignRight ? 'flex-end' : 'flex-start',
    width: alignRight ? '55%' : '70%',
    height: 40,
    borderRadius: alignRight ? '12px 12px 4px 12px' : '4px 12px 12px 12px',
    backgroundColor: 'var(--bg-elevated)',
    animation: 'pulse 1.5s ease-in-out infinite',
  }),
};

function statusColor(state: SourceState): string {
  switch (state) {
    case 'searching': return 'var(--accent)';
    case 'live': return 'var(--success)';
    case 'demo': return 'var(--warning)';
    case 'empty': return 'var(--text-muted)';
    case 'unavailable': return 'var(--danger)';
    default: return 'var(--text-muted)';
  }
}

function statusIcon(state: SourceState): string {
  switch (state) {
    case 'searching': return '⟳';
    case 'live': return '✓';
    case 'demo': return '◑';
    case 'empty': return '∅';
    case 'unavailable': return '✗';
    default: return '○';
  }
}

/** Short honest label for a source's final state (empty string while idle/searching). */
function sourceStateLabel(
  i: { srcLive: string; srcEmpty: string; srcUnavailable: string },
  state: SourceState,
): string {
  switch (state) {
    case 'live': return i.srcLive;
    case 'empty': return i.srcEmpty;
    case 'unavailable': return i.srcUnavailable;
    default: return '';
  }
}

const IDLE_SOURCES: Record<string, SourceState> = Object.fromEntries(
  SOURCES.map((s) => [s, 'idle' as SourceState]),
);

export function ChatPage({ lang }: ChatPageProps) {
  const i = I18N[lang].chat;
  const navigate = useNavigate();
  const { searchId: routeSearchId } = useParams<{ searchId?: string }>();

  // ── State ──────────────────────────────────────────────────────────────────
  const [messages, setMessages] = useState<Message[]>([makeInitialMessage(lang)]);
  const [input, setInput] = useState('');
  const [isThinking, setIsThinking] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(!!routeSearchId);
  const [search, setSearch] = useState<SearchRecord | null>(null);
  const [searchId, setSearchId] = useState<string | null>(routeSearchId ?? null);
  const [searchStatus, setSearchStatus] = useState<SearchStatus>('pending');
  const [sourceStates, setSourceStates] = useState<Record<string, SourceState>>(IDLE_SOURCES);
  const [sendError, setSendError] = useState<string | null>(null);
  const [isStarting, setIsStarting] = useState(false);
  // P1: quick-reply chips for the current clarifier turn (ephemeral, not persisted).
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const pollRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pollAttemptsRef = useRef(0);
  const prevStatusRef = useRef<SearchStatus>('pending');
  const { toast, showToast, dismissToast } = useToast();

  // Auto-scroll to newest message
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // ── Poll search status when running ───────────────────────────────────────
  // Bounded so a stuck background job never spins the UI forever.
  const MAX_POLL_ATTEMPTS = 60; // 60 × 3s = ~3 min ceiling
  const pollSearchStatus = useCallback(
    (id: string) => {
      pollRef.current = setTimeout(async () => {
        try {
          const updated = await getSearch(id);
          setSearch(updated);
          setSearchStatus(updated.status as SearchStatus);

          const serverStatuses = updated.sourceStatuses as Record<string, SourceState> | undefined;

          if (updated.status === 'running') {
            if (serverStatuses && Object.keys(serverStatuses).length > 0) {
              setSourceStates((prev) => ({ ...prev, ...serverStatuses }));
            }
            pollAttemptsRef.current += 1;
            if (pollAttemptsRef.current >= MAX_POLL_ATTEMPTS) {
              // Background job is taking too long — surface a clear failed state.
              setSearchStatus('failed');
              setSourceStates((prev) => {
                const next = { ...prev };
                for (const src of Object.keys(IDLE_SOURCES)) {
                  if (next[src] === 'searching' || next[src] === 'idle') next[src] = 'unavailable';
                }
                return next;
              });
              return;
            }
            pollSearchStatus(id);
          } else if (updated.status === 'completed') {
            setSourceStates((prev) => {
              const next = { ...prev };
              for (const src of Object.keys(IDLE_SOURCES)) {
                // Resolved-but-unreported → honest 'empty', not a fake success.
                next[src] = serverStatuses?.[src] ?? 'empty';
              }
              return next;
            });
          } else if (updated.status === 'failed') {
            setSourceStates((prev) => {
              const next = { ...prev };
              for (const src of Object.keys(IDLE_SOURCES)) {
                next[src] = serverStatuses?.[src] ?? 'unavailable';
              }
              return next;
            });
          }
        } catch {
          // ignore polling errors
        }
      }, 3000);
    },
    [],
  );

  // ── Load existing search + message history when opening by ID ─────────────
  useEffect(() => {
    if (!routeSearchId) return;

    let mounted = true;
    setHistoryLoading(true);

    void (async () => {
      try {
        const [existingSearch, { messages: apiMessages }] = await Promise.all([
          getSearch(routeSearchId),
          listMessages(routeSearchId),
        ]);

        if (!mounted) return;

        setSearch(existingSearch);
        setSearchId(routeSearchId);
        rememberSearch(routeSearchId);
        setSearchStatus(existingSearch.status as SearchStatus);

        // Restore source statuses
        const serverStatuses = existingSearch.sourceStatuses as Record<string, SourceState> | undefined;
        if (serverStatuses && Object.keys(serverStatuses).length > 0) {
          setSourceStates((prev) => ({ ...prev, ...serverStatuses }));
        }
        if (existingSearch.status === 'completed') {
          setSourceStates((prev) => {
            const next = { ...prev };
            for (const src of Object.keys(IDLE_SOURCES)) {
              next[src] = serverStatuses?.[src] ?? 'empty';
            }
            return next;
          });
        }

        // Restore chat messages — prefer API history over the local welcome stub
        if (apiMessages.length > 0) {
          setMessages(
            apiMessages.map((m) => ({
              id: m.id,
              role: m.role as 'user' | 'assistant',
              content: m.content,
              ts: m.createdAt,
            })),
          );
        }

        // Resume polling if still running
        if (existingSearch.status === 'running') {
          pollSearchStatus(routeSearchId);
        }
      } catch {
        // On error keep the welcome message so the user can still chat
      } finally {
        if (mounted) setHistoryLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  // Run only when the route param changes (i.e., user navigates to a different search)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeSearchId]);

  // Start polling when searchId transitions to running from a new send
  useEffect(() => {
    if (searchId && searchStatus === 'running') {
      pollAttemptsRef.current = 0; // fresh poll cycle — reset the bound
      pollSearchStatus(searchId);
    }
    return () => {
      if (pollRef.current) clearTimeout(pollRef.current);
    };
  }, [searchId, searchStatus, pollSearchStatus]);

  // ── Send message ──────────────────────────────────────────────────────────
  // `override` is set when the user taps a quick-reply chip (P1).
  async function handleSend(override?: string) {
    const text = (override ?? input).trim();
    if (!text || isThinking) return;

    setSendError(null);
    setSuggestions([]); // chips belong to the previous question — clear on send

    const userMsg: Message = {
      id: `local-${Date.now()}`,
      role: 'user',
      content: text,
      ts: new Date().toISOString(),
    };
    setMessages((m) => [...m, userMsg]);
    setInput('');
    setIsThinking(true);

    try {
      let activeSearchId = searchId;

      if (!activeSearchId) {
        const newSearch = await createSearch(
          text.slice(0, 100),
          { role: text.slice(0, 100), skills: ['general'] },
        );
        activeSearchId = newSearch.id;
        setSearchId(newSearch.id);
        rememberSearch(newSearch.id);
        setSearch(newSearch);
        navigate(`/chat/${newSearch.id}`, { replace: true });
      }

      const result = await sendMessage(activeSearchId, text);

      const assistantMsg: Message = {
        id: result.message.id,
        role: 'assistant',
        content: result.message.content,
        ts: result.message.createdAt,
      };
      setMessages((m) => [...m, assistantMsg]);
      setSearchStatus(result.searchStatus);
      // Show this turn's tappable options (none on confirmation).
      setSuggestions(result.suggestedAnswers ?? []);

      if (result.confirmed || result.searchStatus !== 'pending') {
        const updatedSearch = await getSearch(activeSearchId);
        setSearch(updatedSearch);
        setSearchStatus(updatedSearch.status as SearchStatus);
        if (updatedSearch.status === 'running') {
          pollSearchStatus(activeSearchId);
        }
      }
    } catch (e) {
      setSendError(e instanceof Error ? e.message : 'Failed to send message');
    } finally {
      setIsThinking(false);
    }
  }

  // ── Start the search once the user is happy with the criteria ─────────────
  async function handleRun() {
    if (!searchId || isStarting) return;
    setSendError(null);
    setIsStarting(true);
    try {
      const updated = await runSearch(searchId);
      setSearch(updated);
      setSearchStatus(updated.status as SearchStatus);
      if (updated.status === 'running') pollSearchStatus(searchId);
    } catch (e) {
      setSendError(e instanceof Error ? e.message : 'Failed to start search');
    } finally {
      setIsStarting(false);
    }
  }

  // ── Retry a failed search (re-runs the background fan-out) ─────────────────
  async function handleRetry() {
    if (!searchId || isStarting) return;
    setSendError(null);
    setIsStarting(true);
    try {
      const updated = await retrySearch(searchId);
      setSearch(updated);
      setSearchStatus(updated.status as SearchStatus);
      setSourceStates(IDLE_SOURCES); // fresh run — clear the previous failure marks
      if (updated.status === 'running') {
        pollAttemptsRef.current = 0;
        pollSearchStatus(searchId);
      }
    } catch (e) {
      setSendError(e instanceof Error ? e.message : 'Failed to retry search');
    } finally {
      setIsStarting(false);
    }
  }

  // ── Toast when search transitions to completed ────────────────────────────
  useEffect(() => {
    if (prevStatusRef.current !== 'completed' && searchStatus === 'completed') {
      showToast(i.searchCompleteToast ?? 'Search complete! Check the results.', 'success');
    }
    prevStatusRef.current = searchStatus;
  }, [searchStatus, showToast, i]);

  // ── Derived state ─────────────────────────────────────────────────────────
  const criteria = search?.criteria as Record<string, unknown> | undefined;
  // Show criteria panel when criteria exist and have at least one non-generic skill
  const skills = criteria?.['skills'] as string[] | undefined;
  const hasCriteria = !!criteria &&
    Object.keys(criteria).length > 0 &&
    (skills === undefined || skills.length === 0 || skills.some((s) => s !== 'general'));
  const isReady = searchStatus === 'ready';
  const isRunning = searchStatus === 'running';
  const isCompleted = searchStatus === 'completed';
  const isFailed = searchStatus === 'failed';
  const isBusy = isThinking || isRunning;

  return (
    <>
    {toast !== null && (
      <Toast
        message={toast.message}
        {...(toast.type !== undefined ? { type: toast.type } : {})}
        onClose={dismissToast}
      />
    )}
    <div style={S.page}>
      {/* Chat panel */}
      <div style={S.chatPanel}>
        <div style={S.chatHeader}>
          {i.title}
          {searchId && (
            <span style={S.searchIdBadge}> · {searchId.slice(0, 20)}…</span>
          )}
        </div>

        <div style={S.messages}>
          {historyLoading ? (
            // Skeleton placeholders while history loads
            <>
              <div style={S.skeletonMsg(false)} />
              <div style={S.skeletonMsg(true)} />
              <div style={S.skeletonMsg(false)} />
              <div style={S.skeletonMsg(true)} />
            </>
          ) : (
            messages.map((msg) => (
              <div key={msg.id} style={msg.role === 'user' ? S.msgUser : S.msgAssistant}>
                {msg.role === 'user' ? msg.content : <AgentMarkdown text={cleanAgentText(msg.content)} />}
              </div>
            ))
          )}
          {suggestions.length > 0 && !isThinking && searchStatus === 'pending' && (
            <div style={S.suggestionRow}>
              {suggestions.map((s) => (
                <button
                  key={s}
                  type="button"
                  style={{ ...S.suggestionChip, opacity: isBusy ? 0.5 : 1 }}
                  disabled={isBusy}
                  onClick={() => void handleSend(s)}
                >
                  {s}
                </button>
              ))}
            </div>
          )}
          {sendError !== null && (
            <div style={S.errorMsg}>⚠ {sendError}</div>
          )}
          {isThinking && (
            <div style={{ ...S.msgAssistant, color: 'var(--text-muted)' }}>
              <IconSpinner size={14} /> {i.thinking}
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        <div style={S.inputRow}>
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                void handleSend();
              }
            }}
            placeholder={i.placeholder}
            rows={2}
            disabled={isBusy || isCompleted}
            style={S.textarea}
          />
          <button
            type="button"
            onClick={() => void handleSend()}
            disabled={!input.trim() || isBusy || isCompleted}
            style={{ ...S.sendBtn, opacity: !input.trim() || isBusy ? 0.5 : 1 }}
            aria-label={i.send}
          >
            <IconSend size={15} />
          </button>
        </div>
      </div>

      {/* Right panel */}
      <div style={S.rightPanel}>
        {/* Criteria card */}
        <div style={S.card}>
          <div style={S.cardHeader}>{i.criteria}</div>
          <div style={S.cardBody}>
            {!hasCriteria ? (
              <div style={S.criteriaEmpty}>{i.criteriaEmpty}</div>
            ) : (
              <div style={S.criteriaRow}>
                {criteria?.['role'] !== undefined && (
                  <div style={S.criteriaItem}>
                    <span style={S.criteriaLabel}>{i.critRole}</span>
                    <span style={S.criteriaValue}>{String(criteria['role'])}</span>
                  </div>
                )}
                {Array.isArray(criteria?.['skills']) && (criteria['skills'] as string[]).length > 0 && (
                  <div style={S.criteriaItem}>
                    <span style={S.criteriaLabel}>{i.critSkills}</span>
                    <span style={S.criteriaValue}>{(criteria['skills'] as string[]).join(', ')}</span>
                  </div>
                )}
                {criteria?.['location'] !== undefined && (
                  <div style={S.criteriaItem}>
                    <span style={S.criteriaLabel}>{i.critLocation}</span>
                    <span style={S.criteriaValue}>{String(criteria['location'])}</span>
                  </div>
                )}
                {criteria?.['experienceYears'] !== undefined && (
                  <div style={S.criteriaItem}>
                    <span style={S.criteriaLabel}>{i.critExperience}</span>
                    <span style={S.criteriaValue}>{String(criteria['experienceYears'])}+ {i.critYears}</span>
                  </div>
                )}
                {criteria?.['remote'] !== undefined && (
                  <div style={S.criteriaItem}>
                    <span style={S.criteriaLabel}>{i.critRemote}</span>
                    <span style={S.criteriaValue}>{criteria['remote'] ? i.critYes : i.critNo}</span>
                  </div>
                )}
                {typeof criteria?.['include'] === 'string' && (criteria['include'] as string).trim() !== '' && (
                  <div style={S.criteriaItem}>
                    <span style={S.criteriaLabel}>{i.critInclude}</span>
                    <span style={S.criteriaValue}>{String(criteria['include'])}</span>
                  </div>
                )}
                {typeof criteria?.['exclude'] === 'string' && (criteria['exclude'] as string).trim() !== '' && (
                  <div style={S.criteriaItem}>
                    <span style={S.criteriaLabel}>{i.critExclude}</span>
                    <span style={S.criteriaValue}>{String(criteria['exclude'])}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Sources card */}
        <div style={S.card}>
          <div style={S.cardHeader}>{i.sources}</div>
          <div style={S.cardBody}>
            {SOURCES.map((src) => {
              const state = sourceStates[src] ?? 'idle';
              // Telegram only ever returns synthetic data; LinkedIn/UA-board
              // assist sources surface public search results (link-out only).
              const isDemo = isDemoSource(src) || state === 'demo';
              const isAssist = isAssistSource(src);
              return (
                <div key={src} style={S.sourceRow}>
                  <SourceBadge sourceId={src} showLabel />
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    {isDemo && <span style={S.demoTag} title="Synthetic demo data">{i.demo}</span>}
                    {isAssist && <span style={S.assistTag} title="Public search — open & import manually">{i.assist}</span>}
                    <span style={{ ...S.sourceStateLabel, color: statusColor(state) }}>
                      {sourceStateLabel(i, state)}
                    </span>
                    <span
                      style={{
                        ...S.sourceStatus,
                        color: statusColor(state),
                        fontFamily: 'var(--font-mono)',
                      }}
                    >
                      {statusIcon(state)}
                    </span>
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Status / action buttons */}
        {isReady && searchId && (
          <button
            type="button"
            onClick={() => void handleRun()}
            disabled={isStarting}
            style={{ ...S.actionBtn, opacity: isStarting ? 0.6 : 1 }}
          >
            {isStarting ? i.searching : i.runSearch}
          </button>
        )}
        {isRunning && (
          <button type="button" disabled style={{ ...S.actionBtn, opacity: 0.6 }}>
            {i.searching}
          </button>
        )}
        {isCompleted && searchId && (
          <>
            <button
              type="button"
              onClick={() => navigate(`/results/${searchId}`)}
              style={{ ...S.actionBtn, backgroundColor: 'var(--success, #38a169)' }}
            >
              {i.viewResults}
            </button>
            <button
              type="button"
              onClick={() => navigate('/chat')}
              style={S.newSearchBtn}
            >
              {i.newSearch}
            </button>
          </>
        )}
        {isFailed && searchId && (
          <>
            <button
              type="button"
              onClick={() => void handleRetry()}
              disabled={isStarting}
              style={{ ...S.actionBtn, backgroundColor: 'var(--danger, #e53e3e)', opacity: isStarting ? 0.6 : 1 }}
            >
              {isStarting ? i.searching : i.searchFailed}
            </button>
            <button
              type="button"
              onClick={() => navigate('/chat')}
              style={S.newSearchBtn}
            >
              {i.newSearch}
            </button>
          </>
        )}
      </div>
    </div>
    </>
  );
}
